import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Card, Button, Carousel, Modal, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { UPLOAD_BASE_URL, BASE_URL } from '../../services/api';
import QuickStatsBar from '../../components/QuickStatsBar';
import AnnouncementPopup from '../../components/AnnouncementPopup'; // Import AnnouncementPopup
import 'animate.css/animate.min.css';

// Import icons
import { 
  FaPause, 
  FaMapMarkedAlt,
  FaWifi,
  FaServer,
  FaCalendarAlt,
  FaPaperclip,
  FaDownload,
  FaUniversity,
  FaBuilding,
  FaArrowRight,
  FaGraduationCap,
  FaCheck,
  FaBullhorn,
  FaStar,
  FaPlay,
  FaBookOpen,
  FaChalkboardTeacher,
  FaFlask,
  FaLaptopCode,
  FaBriefcase,
  FaUserGraduate,
  FaGlobeAmericas,
  FaNetworkWired,
  FaRegCalendarCheck,
  FaImages,
  FaBolt,
  FaGem,
  FaTrophy,
  FaAward,
  FaLightbulb,
  FaRocket,
  FaBrain,
  FaMicroscope,
  FaUsers,
  FaChartLine,
  FaHandshake
} from 'react-icons/fa';

const Home = () => {
  const [homeData, setHomeData] = useState(null);
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSlides, setLoadingSlides] = useState(true);
  const [error, setError] = useState('');
  const [slidesError, setSlidesError] = useState('');

  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorType, setErrorType] = useState('');
  const [hasShownError, setHasShownError] = useState(false);

  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isVisible, setIsVisible] = useState({});
  const observerRef = useRef(null);

  const API_BASE_URL = BASE_URL;

  // Educational color schemes
  const eduColors = {
    primary: '#1e3c72',
    secondary: '#2a5298',
    accent: '#4a7fba',
    success: '#27ae60',
    warning: '#f39c12',
    danger: '#e74c3c',
    light: '#f8f9fa',
    dark: '#2c3e50'
  };

  // Educational gradients
  const eduGradients = [
    'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #4a7fba 100%)',
    'linear-gradient(135deg, #27ae60 0%, #2ecc71 50%, #16a085 100%)',
    'linear-gradient(135deg, #8e44ad 0%, #9b59b6 50%, #8e44ad 100%)',
    'linear-gradient(135deg, #d35400 0%, #e67e22 50%, #f39c12 100%)',
    'linear-gradient(135deg, #2980b9 0%, #3498db 50%, #2980b9 100%)',
    'linear-gradient(135deg, #16a085 0%, #1abc9c 50%, #16a085 100%)'
  ];

  const showErrorModalHandler = useCallback((message, type) => {
    if (!hasShownError) {
      setErrorMessage(message);
      setErrorType(type);
      setShowErrorModal(true);
      setHasShownError(true);
    }
  }, [hasShownError]);

  const fetchHomeData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_BASE_URL}/home.php`, {
        timeout: 10000
      });
      
      if (response.data.success) {
        setHomeData(response.data.data);
      } else {
        setError('Failed to load home page data');
        showErrorModalHandler('The server encountered an error while processing your request. Please try again later.', 'server');
      }
    } catch (err) {
      console.error('API Error:', err);
      
      if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        showErrorModalHandler('Unable to connect to the server. Please check your internet connection.', 'internet');
      } else if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        showErrorModalHandler('The server is taking too long to respond. Please check your connection or try again later.', 'server');
      } else if (err.response) {
        showErrorModalHandler(`Server error: ${err.response.status}. Please try again later.`, 'server');
      } else {
        showErrorModalHandler('An unexpected error occurred. Please try again.', 'server');
      }
      
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, showErrorModalHandler]);

  const fetchSlides = useCallback(async () => {
    try {
      setLoadingSlides(true);
      setSlidesError('');
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const response = await fetch(`${API_BASE_URL}/slider.php?action=getActive`, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        setSlides(result.data || []);
      } else {
        setSlidesError(result.message || 'Failed to load slides');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setSlidesError('Unable to load slides');
    } finally {
      setLoadingSlides(false);
    }
  }, [API_BASE_URL]);

  // Mouse movement effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: e.clientX,
        y: e.clientY
      });
    };

    const handleScroll = () => {
      const scrolled = window.scrollY;
      const maxScroll = document.body.scrollHeight - window.innerHeight;
      const progress = (scrolled / maxScroll) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Intersection Observer for animations
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(prev => ({
            ...prev,
            [entry.target.id]: entry.isIntersecting
          }));
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -100px 0px' }
    );

    const sections = document.querySelectorAll('.edu-animate-section');
    sections.forEach((section) => {
      if (section.id) {
        observerRef.current.observe(section);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [homeData]);

  useEffect(() => {
    fetchHomeData();
    fetchSlides();
  }, [fetchHomeData, fetchSlides]);

  const getFileType = (file) => {
    if (!file) return '';
    const ext = file.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
    if (ext === 'pdf') return 'pdf';
    return 'other';
  };

  const retryConnection = () => {
    setShowErrorModal(false);
    setErrorMessage('');
    setErrorType('');
    setHasShownError(false);
    
    if (!homeData) {
      setLoading(true);
      fetchHomeData();
    }
    
    if (slides.length === 0) {
      setLoadingSlides(true);
      fetchSlides();
    }
  };

  const formatDate = (dateString) => {
    const options = { day: 'numeric', month: 'short', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const renderIcon = (iconString, props = {}) => {
    const iconMap = {
      'fas fa-project-diagram': <FaNetworkWired {...props} />,
      'fas fa-user': <FaUserGraduate {...props} />,
      'fas fa-users': <FaUsers {...props} />,
      'fas fa-user-tie': <FaChalkboardTeacher {...props} />,
      'fas fa-graduation-cap': <FaGraduationCap {...props} />,
      'fas fa-flask': <FaFlask {...props} />,
      'fas fa-chalkboard-teacher': <FaChalkboardTeacher {...props} />,
      'fas fa-book': <FaBookOpen {...props} />,
      'fas fa-laptop-code': <FaLaptopCode {...props} />,
      'fas fa-briefcase': <FaBriefcase {...props} />,
      'fas fa-newspaper': <FaBookOpen {...props} />,
      'fas fa-bullhorn': <FaBullhorn {...props} />,
      'fas fa-calendar-alt': <FaCalendarAlt {...props} />,
      'fas fa-download': <FaDownload {...props} />,
      'fas fa-credit-card': <FaBriefcase {...props} />,
      'fas fa-images': <FaImages {...props} />,
      'fas fa-wifi-slash': <FaWifi {...props} />,
      'fas fa-server': <FaServer {...props} />,
      'fas fa-arrow-right': <FaArrowRight {...props} />,
      'fas fa-check': <FaCheck {...props} />,
      'fas fa-star': <FaStar {...props} />,
      'fas fa-play': <FaPlay {...props} />,
      'fas fa-paperclip': <FaPaperclip {...props} />,
      'fas fa-building': <FaBuilding {...props} />,
      'fas fa-university': <FaUniversity {...props} />,
      'fas fa-globe-americas': <FaGlobeAmericas {...props} />,
      'fas fa-reg-calendar-check': <FaRegCalendarCheck {...props} />,
      'fas fa-pause': <FaPause {...props} />,
      'fas fa-map-marked-alt': <FaMapMarkedAlt {...props} />,
      'fas fa-bolt': <FaBolt {...props} />,
      'fas fa-gem': <FaGem {...props} />,
      'fas fa-trophy': <FaTrophy {...props} />,
      'fas fa-award': <FaAward {...props} />,
      'fas fa-lightbulb': <FaLightbulb {...props} />,
      'fas fa-rocket': <FaRocket {...props} />,
      'fas fa-brain': <FaBrain {...props} />,
      'fas fa-microscope': <FaMicroscope {...props} />,
      'fas fa-chart-line': <FaChartLine {...props} />,
      'fas fa-handshake': <FaHandshake {...props} />
    };

    return iconMap[iconString] || <FaUniversity {...props} />;
  };

  if (loading && !homeData && !slides.length && !slidesError) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center edu-loading-screen">
        <div className="text-center position-relative">
          <div className="edu-loader-animation">
            <div className="edu-loader-orbit edu-orbit-1"></div>
            <div className="edu-loader-orbit edu-orbit-2"></div>
            <div className="edu-loader-orbit edu-orbit-3"></div>
            <div className="edu-loader-center">
              <FaGraduationCap className="edu-loader-icon" />
            </div>
          </div>
          <div className="mt-4 edu-loading-text">
            <h4 className="text-primary fw-bold mb-3">Loading Excellence...</h4>
            <div className="edu-loading-dots">
              <span className="dot dot-1"></span>
              <span className="dot dot-2"></span>
              <span className="dot dot-3"></span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderSlider = () => {
    if (loadingSlides && slides.length === 0) {
      return (
        <div className="edu-hero-loading d-flex align-items-center justify-content-center">
          <div className="text-center">
            <div className="edu-spinner-container">
              <FaBookOpen className="edu-spinner-icon" />
            </div>
            <p className="mt-3 text-white">Preparing Your Educational Journey...</p>
          </div>
        </div>
      );
    }

    if (slides.length > 0) {
      return (
        <Carousel fade controls indicators interval={5000} className="edu-hero-carousel">
          {slides.map((slide, index) => (
            <Carousel.Item key={slide.id}>
              <div 
                className="edu-carousel-slide"
                style={{
                  // backgroundImage: `linear-gradient(rgba(30, 60, 114, 0.8), rgba(30, 60, 114, 0.9)), url(${slide.image_url.startsWith('http') ? slide.image_url : `${UPLOAD_BASE_URL}/${slide.image_url}`})`,
                  backgroundImage: ` url(${slide.image_url.startsWith('http') ? slide.image_url : `${UPLOAD_BASE_URL}/${slide.image_url}`})`,
                }}
              >
                <Container>
                  <Row className="align-items-center min-vh-85">
                    <Col lg={8} className="text-white">
                      <div className="edu-slide-badges mb-4">
                        <Badge bg="light" text="dark" className="edu-badge me-2 animate__animated animate__fadeIn">
                          <FaUniversity className="me-2" /> Excellence
                        </Badge>
                        <Badge bg="primary" className="edu-badge animate__animated animate__fadeIn animate__delay-1s">
                          <FaCalendarAlt className="me-2" /> {new Date().getFullYear()}
                        </Badge>
                      </div>
                      <h1 className="edu-hero-title animate__animated animate__fadeInUp">
                        {slide.title}
                      </h1>
                      <p className="edu-hero-subtitle animate__animated animate__fadeInUp animate__delay-1s">
                        {slide.subtitle}
                      </p>
                      {slide.button_text && slide.button_text.trim() !== '' && (
                        <div className="edu-hero-buttons mt-4">
                          <Button 
                            as={Link} 
                            to={slide.button_link || '/'}
                            variant="light" 
                            size="lg" 
                            className="edu-btn-primary animate__animated animate__fadeInUp animate__delay-2s"
                          >
                            {renderIcon('fas fa-arrow-right', { className: 'me-2' })}
                            {slide.button_text}
                          </Button>
                          {/* <Button 
                            variant="outline-light" 
                            size="lg" 
                            className="edu-btn-outline animate__animated animate__fadeInUp animate__delay-2s ms-3"
                          >
                            {renderIcon('fas fa-play', { className: 'me-2' })}
                            Virtual Tour
                          </Button> */}
                        </div>
                      )}
                    </Col>
                  </Row>
                </Container>
                <div className="edu-floating-elements">
                  {[FaBookOpen, FaGraduationCap, FaFlask, FaLaptopCode, FaBrain].map((Icon, i) => (
                    <Icon key={i} className={`edu-floating-icon edu-icon-${i + 1}`} />
                  ))}
                </div>
                <div className="edu-slide-overlay"></div>
              </div>
            </Carousel.Item>
          ))}
        </Carousel>
      );
    }

    return (
      <div className="edu-default-hero">
        <Container className="h-100">
          <Row className="align-items-center h-100">
            <Col lg={8} className="text-center text-lg-start">
              <Badge bg="primary" className="edu-badge mb-3">
                {renderIcon('fas fa-university', { className: 'me-2' })} Welcome
              </Badge>
              <h1 className="edu-hero-title text-white">Education Excellence Portal</h1>
              <p className="edu-hero-subtitle text-white">
                Empowering Minds, Shaping Futures Through Quality Education
              </p>
              <div className="edu-hero-buttons">
                <Button as={Link} to="/about" variant="light" size="lg" className="edu-btn-primary">
                  {renderIcon('fas fa-graduation-cap', { className: 'me-2' })}
                  Explore Academics
                </Button>
                <Button variant="outline-light" size="lg" className="edu-btn-outline ms-3">
                  {renderIcon('fas fa-map-marked-alt', { className: 'me-2' })}
                  Campus Map
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
        <div className="edu-floating-elements">
          {[FaBookOpen, FaGraduationCap, FaFlask, FaLaptopCode, FaBrain].map((Icon, i) => (
            <Icon key={i} className={`edu-floating-icon edu-icon-${i + 1}`} />
          ))}
        </div>
        <div className="edu-slide-overlay"></div>
      </div>
    );
  };

  return (
    <>
      {/* Animated Progress Bar */}
      <div className="edu-scroll-progress">
        <div 
          className="edu-progress-fill"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {/* Enhanced Error Modal */}
      <Modal
        show={showErrorModal}
        onHide={() => setShowErrorModal(false)}
        size="md"
        centered
        backdrop="static"
        className="edu-modal"
      >
        <Modal.Header closeButton className="border-0 edu-modal-header">
          <Modal.Title className="text-white">
            <div className="d-flex align-items-center">
              {errorType === 'internet' ? 
                renderIcon('fas fa-wifi-slash', { className: 'me-3 edu-modal-icon' }) : 
                renderIcon('fas fa-server', { className: 'me-3 edu-modal-icon' })
              }
              <div>
                <h4 className="mb-0">System Alert</h4>
                <small>Technical Difficulty</small>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="text-center mb-4">
            <div className="edu-error-graphic mb-3">
              {renderIcon('fas fa-exclamation-triangle', { className: 'text-warning fa-3x' })}
              <div className="edu-error-ring ring-1"></div>
              <div className="edu-error-ring ring-2"></div>
            </div>
            <p className="lead">{errorMessage}</p>
          </div>
          <div className="d-flex justify-content-between gap-3">
            <Button variant="outline-secondary" onClick={() => setShowErrorModal(false)}>
              Close
            </Button>
            <Button 
              variant="primary" 
              onClick={retryConnection}
              className="edu-btn-primary"
            >
              {renderIcon('fas fa-redo', { className: 'me-2' })}
              Retry Connection
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Hero Section */}
      <section className="edu-hero-section">
        {renderSlider()}
      </section>

      {/* Dynamic Quick Stats Bar */}
      <QuickStatsBar 
        stats={homeData?.quickStats} 
        loading={loading} 
        error={error}
        className="py-4 edu-stats-bar"
      />

      {/* Quick Links Section */}
      {homeData?.quickLinks && homeData.quickLinks.length > 0 && (
        <section 
          id="quick-links" 
          className="py-5 edu-animate-section"
          style={{ background: `linear-gradient(135deg, ${eduColors.light} 0%, #ffffff 100%)` }}
        >
          <Container>
            <div className="text-center mb-5">
              <div className="edu-section-header mb-4">
                {renderIcon('fas fa-bolt', { className: 'edu-section-icon' })}
                <h2 className="edu-section-title">Quick Academic Links</h2>
              </div>
              <p className="edu-section-subtitle">Access essential academic resources and services</p>
            </div>
            <Row className="g-4">
              {homeData.quickLinks.map((link, index) => (
                <Col lg={3} md={6} key={link.id}>
                  <div 
                    className={`edu-card-wrapper ${isVisible['quick-links'] ? 'edu-visible' : ''}`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <Card 
                      as={Link} 
                      to={link.link} 
                      className="edu-card edu-hover-lift"
                      style={{ 
                        background: eduGradients[index % eduGradients.length],
                        backgroundSize: '200% 200%'
                      }}
                    >
                      <Card.Body className="p-4 text-center position-relative">
                        <div className="edu-card-icon mb-4">
                          <div className="edu-icon-wrapper">
                            {renderIcon(link.icon || 'fas fa-link')}
                            <div className="edu-icon-glow"></div>
                          </div>
                        </div>
                        <Card.Title className="mb-0 fw-bold text-white edu-card-title">
                          {link.title}
                        </Card.Title>
                        <div className="edu-card-arrow mt-3">
                          {renderIcon('fas fa-arrow-right', { className: 'text-white' })}
                        </div>
                        <div className="edu-card-line"></div>
                      </Card.Body>
                    </Card>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>
      )}

      {/* Features Section with Enhanced Animation */}
      {homeData?.features && homeData.features.length > 0 && (
        <section 
          id="features" 
          className="py-5 edu-animate-section position-relative"
          style={{ background: `linear-gradient(45deg, rgba(30, 60, 114, 0.03) 25%, transparent 25%, transparent 75%, rgba(30, 60, 114, 0.03) 75%)` }}
        >
          <div className="edu-floating-icons">
            {[FaBookOpen, FaGraduationCap, FaFlask, FaChartLine].map((Icon, i) => (
              <Icon key={i} className={`edu-floating-bg-icon edu-bg-icon-${i + 1}`} />
            ))}
          </div>
          <Container>
            <div className="text-center mb-5">
              <div className="edu-section-header mb-4">
                {renderIcon('fas fa-gem', { className: 'edu-section-icon' })}
                <h2 className="edu-section-title">Academic Programs</h2>
              </div>
              <p className="edu-section-subtitle">Comprehensive programs designed for academic excellence</p>
            </div>
            <Row>
              {homeData.features.map((feature, index) => (
                <Col lg={4} md={6} className="mb-4" key={feature.id}>
                  <div 
                    className={`edu-card-wrapper ${isVisible['features'] ? 'edu-visible' : ''}`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="edu-feature-card edu-hover-3d">
                      <div className="edu-feature-icon">
                        <div className="edu-icon-wrapper">
                          {renderIcon(feature.icon || 'fas fa-check')}
                          <div className="edu-icon-ring"></div>
                        </div>
                      </div>
                      <h3 className="edu-feature-title">{feature.title}</h3>
                      <p className="edu-feature-description">{feature.description}</p>
                      <div className="edu-feature-hover">
                        <span>Explore Program</span>
                        {renderIcon('fas fa-arrow-right')}
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>
      )}

      {/* About Section with Interactive Elements */}
      {homeData?.about && (
        <section 
          id="about" 
          className="py-5 edu-animate-section position-relative"
          style={{ background: eduGradients[0], backgroundSize: '200% 200%' }}
        >
          <div className="edu-pattern-overlay"></div>
          <Container className="position-relative">
            <Row className="align-items-center">
              <Col lg={6} className="mb-5 mb-lg-0">
                <Badge bg="light" text="dark" className="edu-badge mb-3">
                  {renderIcon('fas fa-university', { className: 'me-2' })} ABOUT INSTITUTION
                </Badge>
                <h2 className="edu-section-title text-white mb-4">
                  {homeData.about.section_title}
                </h2>
                <p className="edu-section-subtitle text-white mb-4">
                  {homeData.about.content}
                </p>
                {/* <div className="edu-stats-container mb-4">
                  <div className="edu-stat-item">
                    <h3 className="text-warning mb-0">150+</h3>
                    <small className="text-white">Programs</small>
                  </div>
                  <div className="edu-stat-item">
                    <h3 className="text-warning mb-0">500+</h3>
                    <small className="text-white">Faculty</small>
                  </div>
                  <div className="edu-stat-item">
                    <h3 className="text-warning mb-0">25K+</h3>
                    <small className="text-white">Students</small>
                  </div>
                </div> */}
                <div className="d-flex gap-3">
                  <Button 
                    as={Link} 
                    to={homeData.about.button_link || '/about'} 
                    variant="light" 
                    size="lg" 
                    className="edu-btn-primary"
                  >
                    {renderIcon('fas fa-compass', { className: 'me-2' })}
                    {homeData.about.button_text}
                  </Button>
                  {/* <Button 
                    variant="outline-light" 
                    size="lg" 
                    className="edu-btn-outline"
                  >
                    {renderIcon('fas fa-play', { className: 'me-2' })}
                    Virtual Tour
                  </Button> */}
                </div>
              </Col>
              <Col lg={6}>
                <div className="edu-image-container">
                  <div className="edu-image-wrapper">
                    {homeData?.about?.image_url?.trim() ? (
                      <img 
                        src={`${UPLOAD_BASE_URL}${homeData.about.image_url}`}
                        alt="About Institution" 
                        className="edu-image"
                      />
                    ) : (
                      <div className="edu-image-placeholder">
                        {renderIcon('fas fa-university', { className: 'fa-4x text-white opacity-75' })}
                      </div>
                    )}
                    <div className="edu-image-frame"></div>
                  </div>
                  <div className="edu-image-badge">
                    {renderIcon('fas fa-trophy', { className: 'me-2' })}
                    <span>Accredited Institution</span>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>
      )}

      {/* Announcements Section */}
{homeData?.announcements && homeData.announcements.length > 0 && (
  <section 
    id="announcements" 
    className="py-5 edu-animate-section"
    style={{ background: '#f8f9fa' }}
  >
    <Container>
      <div className="text-center mb-5">
        <div className="edu-section-header mb-4">
          {renderIcon('fas fa-bullhorn', { className: 'edu-section-icon' })}
          <h2 className="edu-section-title">Latest Announcements</h2>
        </div>
        <p className="edu-section-subtitle">Stay updated with important news and information</p>
      </div>
      <Row>
        {homeData.announcements.map((announcement, index) => (
          <Col lg={4} md={6} className="mb-4" key={announcement.id}>
            <div 
              className={`edu-card-wrapper ${isVisible['announcements'] ? 'edu-visible' : ''}`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <Card 
                className="edu-card edu-hover-lift"
                style={{ 
                  background: eduGradients[index % eduGradients.length],
                  backgroundSize: '200% 200%'
                }}
              >
                <Card.Body className="p-4">
                  <div className="d-flex align-items-start mb-3">
                    <div className="edu-announcement-icon">
                      {renderIcon(announcement.icon || 'fas fa-bullhorn', { className: 'text-white' })}
                      <div className="edu-icon-pulse"></div>
                    </div>
                    <div className="ms-3">
                      <h5 className="mb-2 text-white edu-card-title">{announcement.title}</h5>
                      <small className="text-white d-block mb-2">
                        {renderIcon('fas fa-calendar-alt', { className: 'me-1' })}
                        {formatDate(announcement.date)}
                      </small>
                    </div>
                  </div>

                  <Card.Text className="text-white edu-card-text">
                    {announcement.content.length > 100
                      ? announcement.content.substring(0, 100) + '...'
                      : announcement.content}
                  </Card.Text>

                  {announcement.attachment && (
                    <div className="mb-3 edu-attachment-badge">
                      {renderIcon('fas fa-paperclip', { className: 'text-warning me-2' })}
                      <span className="text-warning">Attachment Available</span>
                    </div>
                  )}

                  <Button
                    variant="outline-light"
                    className="edu-btn-outline"
                    onClick={() => {
                      setSelectedAnnouncement(announcement);
                      setShowAnnouncementModal(true);
                    }}
                  >
                    Read More {renderIcon('fas fa-arrow-right', { className: 'ms-2' })}
                  </Button>
                </Card.Body>
              </Card>
            </div>
          </Col>
        ))}
      </Row>
      
      {/* View All Announcements Button */}
      <div className="text-center mt-5 pt-4">
        <Button
          as={Link}
          to="/PublicAnnouncements"
          variant="primary"
          size="lg"
          className="edu-btn-primary"
        >
          {renderIcon('fas fa-list', { className: 'me-2' })}
          View All Announcements
        </Button>
      </div>
    </Container>
  </section>
)}
      {/* Team Section */}
      {homeData?.team && homeData.team.length > 0 && (
        <section 
          id="team" 
          className="py-5 edu-animate-section position-relative"
          style={{ background: `linear-gradient(45deg, rgba(44, 62, 80, 0.05) 25%, transparent 25%, transparent 75%, rgba(44, 62, 80, 0.05) 75%)` }}
        >
          <div className="edu-floating-icons">
            {[FaUsers, FaChalkboardTeacher, FaFlask, FaBookOpen].map((Icon, i) => (
              <Icon key={i} className={`edu-floating-bg-icon edu-bg-icon-${i + 5}`} />
            ))}
          </div>
          <Container>
            <div className="text-center mb-5">
              <div className="edu-section-header mb-4">
                {renderIcon('fas fa-users', { className: 'edu-section-icon' })}
                <h2 className="edu-section-title">Our Team</h2>
              </div>
              <p className="edu-section-subtitle">Meet our distinguished team members </p>
            </div>
            <Row>
              {homeData.team.map((member, index) => (
                <Col lg={4} md={6} className="mb-4" key={member.id}>
                  <div 
                    className={`edu-card-wrapper ${isVisible['team'] ? 'edu-visible' : ''}`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="edu-team-card">
                      <div className="edu-team-image">
                        <img 
                          src={
                            member.image_url
                              ? `${UPLOAD_BASE_URL}${member.image_url}`
                              : 'https://cdn-icons-png.flaticon.com/512/9187/9187604.png'
                          }
                          alt={member.name}
                          className="edu-image"
                        />
                        <div className="edu-image-overlay"></div>
                      </div>
                      <div className="edu-team-content">
                        <h3 className="edu-team-name">{member.name}</h3>
                        <p className="edu-team-designation">{member.designation}</p>
                        <p className="edu-team-bio">{member.bio}</p>
                        {member.department && (
                          <div className="edu-team-department">
                            <span>{member.department}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>
      )}

      {/* Testimonials Section */}
      {homeData?.testimonials && homeData.testimonials.length > 0 && (
        <section 
          id="testimonials" 
          className="py-5 edu-animate-section"
          style={{ background: `linear-gradient(135deg, ${eduColors.light} 0%, #ffffff 100%)` }}
        >
          <Container>
            <div className="text-center mb-5">
              <div className="edu-section-header mb-4">
                {renderIcon('fas fa-star', { className: 'edu-section-icon' })}
                <h2 className="edu-section-title">Testimonials</h2>
              </div>
              <p className="edu-section-subtitle">What say about their experience</p>
            </div>
            <Row>
              {homeData.testimonials.map((testimonial, index) => (
                <Col lg={4} md={6} className="mb-4" key={testimonial.id}>
                  <div 
                    className={`edu-card-wrapper ${isVisible['testimonials'] ? 'edu-visible' : ''}`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="edu-testimonial-card">
                      <div className="edu-testimonial-quote">
                        {renderIcon('fas fa-quote-left', { className: 'text-primary' })}
                      </div>
                      <p className="edu-testimonial-text">"{testimonial.content}"</p>
                      <div className="edu-testimonial-author">
                        <div className="edu-author-image">
                          <img 
                            src={
                              testimonial.image_url
                                ? `${UPLOAD_BASE_URL}${testimonial.image_url}`
                                : 'https://cdn-icons-png.flaticon.com/512/9187/9187604.png'
                            }
                            alt={testimonial.name}
                            className="edu-image"
                          />
                        </div>
                        <div className="edu-author-info">
                          <h4 className="edu-author-name">{testimonial.name}</h4>
                          <p className="edu-author-designation">{testimonial.designation}</p>
                          <div className="edu-testimonial-rating">
                            {[...Array(5)].map((_, i) => (
                              <span key={i}>
                                {renderIcon('fas fa-star', { 
                                  className: i < testimonial.rating ? 'text-warning' : 'text-muted' 
                                })}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>
      )}

      {/* Call to Action Section */}
      {homeData?.callToAction && (
        <section 
          id="cta" 
          className="py-5 edu-animate-section position-relative edu-cta-section"
        >
          <div className="edu-cta-overlay"></div>
          <Container className="text-center position-relative">
            <Badge bg="light" text="dark" className="edu-badge mb-3">
              {renderIcon('fas fa-graduation-cap', { className: 'me-2' })} GET STARTED
            </Badge>
            <h2 className="edu-section-title text-white mb-4">
              {homeData.callToAction.title}
            </h2>
            <p className="edu-section-subtitle text-white mb-4">
              {homeData.callToAction.description}
            </p>
            <div className="d-flex flex-wrap justify-content-center gap-3">
              {homeData.callToAction.buttons?.map((button, index) => (
                <Button 
                  key={index} 
                  as={Link} 
                  to={button.link || '/'} 
                  variant={button.variant || 'light'} 
                  size="lg" 
                  className="edu-btn-primary"
                >
                  {renderIcon(button.icon || 'fas fa-graduation-cap', { className: 'me-2' })}
                  {button.text}
                </Button>
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* Announcement Modal */}
      <Modal
        show={showAnnouncementModal}
        onHide={() => setShowAnnouncementModal(false)}
        size="lg"
        centered
        className="edu-modal"
      >
        <Modal.Header closeButton className="border-0 edu-modal-header">
          <Modal.Title>
            <div className="d-flex align-items-center">
              <div className="edu-modal-icon-wrapper me-3">
                {renderIcon('fas fa-bullhorn', { className: 'text-white' })}
                <div className="edu-icon-glow"></div>
              </div>
              <div>
                <h3 className="mb-0 text-white">{selectedAnnouncement?.title}</h3>
                <small className="text-white">University Announcement</small>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-4">
          <div className="mb-4">
            <p className="text-muted">
              {renderIcon('fas fa-calendar-alt', { className: 'me-2 text-primary' })}
              {selectedAnnouncement && formatDate(selectedAnnouncement.date)}
            </p>
          </div>

          <div className="edu-modal-content">
            {selectedAnnouncement?.content.split('\n\n').map((paragraph, index) => (
              <p key={index} className="mb-3">
                {paragraph}
              </p>
            ))}
          </div>

          {selectedAnnouncement?.attachment && (
            <>
              <hr className="my-4" />
              <h6 className="fw-bold mb-3">
                {renderIcon('fas fa-paperclip', { className: 'me-2 text-primary' })}
                Attachment
              </h6>

              {getFileType(selectedAnnouncement.attachment) === 'image' && (
                <div className="mb-3">
                  <img
                    src={`${UPLOAD_BASE_URL}${selectedAnnouncement.attachment}`}
                    alt="Attachment"
                    className="img-fluid rounded edu-image"
                  />
                </div>
              )}

              {getFileType(selectedAnnouncement.attachment) === 'pdf' && (
                <div className="mb-3">
                  <iframe
                    src={`${UPLOAD_BASE_URL}${selectedAnnouncement.attachment}`}
                    width="100%"
                    height="400"
                    title="PDF Preview"
                    className="rounded border"
                  />
                </div>
              )}

              <div className="mt-3">
                <a
                  href={`${UPLOAD_BASE_URL}${selectedAnnouncement.attachment}`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-primary edu-btn-primary"
                >
                  {renderIcon('fas fa-download', { className: 'me-2' })}
                  Download Attachment
                </a>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>


      <style jsx="true">{`
        /* Enhanced Educational Theme */
        :root {
          --edu-primary: ${eduColors.primary};
          --edu-secondary: ${eduColors.secondary};
          --edu-accent: ${eduColors.accent};
          --edu-success: ${eduColors.success};
          --edu-warning: ${eduColors.warning};
          --edu-danger: ${eduColors.danger};
          --edu-light: ${eduColors.light};
          --edu-dark: ${eduColors.dark};
        }

        /* Loading Animation */
        .edu-loading-screen {
          background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
        }

        .edu-loader-animation {
          position: relative;
          width: 120px;
          height: 120px;
          margin: 0 auto;
        }

        .edu-loader-orbit {
          position: absolute;
          border: 2px dashed rgba(255,255,255,0.3);
          border-radius: 50%;
          animation: eduOrbit 20s linear infinite;
        }

        .edu-orbit-1 {
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
        }

        .edu-orbit-2 {
          top: 15px;
          left: 15px;
          right: 15px;
          bottom: 15px;
          animation-direction: reverse;
          animation-duration: 15s;
        }

        .edu-orbit-3 {
          top: 30px;
          left: 30px;
          right: 30px;
          bottom: 30px;
          animation-duration: 10s;
        }

        .edu-loader-center {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 60px;
          height: 60px;
          background: #fff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 0 30px rgba(255,255,255,0.3);
        }

        .edu-loader-icon {
          font-size: 30px;
          color: var(--edu-primary);
          animation: eduPulse 2s ease-in-out infinite;
        }

        .edu-loading-dots {
          display: flex;
          justify-content: center;
          gap: 8px;
          margin-top: 20px;
        }

        .edu-loading-dots .dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: var(--edu-accent);
          animation: eduBounce 1.4s ease-in-out infinite;
        }

        .dot-1 { animation-delay: -0.32s; }
        .dot-2 { animation-delay: -0.16s; }
        .dot-3 { animation-delay: 0s; }

        @keyframes eduOrbit {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes eduPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }

        @keyframes eduBounce {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-10px); }
        }

        /* Hero Section */
        .edu-hero-section {
          position: relative;
          margin-top: -140px;
          padding-top: 140px;
          overflow: hidden;
        }

        .edu-hero-carousel .carousel-indicators button {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          margin: 0 5px;
        }

        .edu-carousel-slide {
          background-size: cover !important;
          background-position: center !important;
          height: 85vh;
          min-height: 600px;
          display: flex;
          align-items: center;
          position: relative;
          overflow: hidden;
          animation: eduGradientShift 15s ease infinite;
        }

        .edu-default-hero {
          height: 85vh;
          min-height: 600px;
          display: flex;
          align-items: center;
          position: relative;
          overflow: hidden;
          background: linear-gradient(135deg, var(--edu-primary) 0%, var(--edu-secondary) 50%, var(--edu-accent) 100%);
          background-size: 200% 200%;
          animation: eduGradientShift 15s ease infinite;
        }

        @keyframes eduGradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        .edu-hero-title {
          font-size: 3.5rem;
          font-weight: 800;
          line-height: 1.2;
          margin-bottom: 1.5rem;
          text-shadow: 0 2px 10px rgba(0,0,0,0.3);
        }

        .edu-hero-subtitle {
          font-size: 1.4rem;
          line-height: 1.6;
          opacity: 0.9;
          max-width: 600px;
        }

        .edu-badge {
          font-size: 0.9rem;
          padding: 8px 16px;
          border-radius: 4px;
          background: rgba(255, 255, 255, 0.15);
          border: 1px solid rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(10px);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        /* Floating Elements */
        .edu-floating-elements {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          overflow: hidden;
        }

        .edu-floating-icon {
          position: absolute;
          font-size: 3rem;
          color: rgba(255, 255, 255, 0.1);
          animation: eduFloat 15s ease-in-out infinite;
        }

        .edu-icon-1 { top: 15%; left: 10%; animation-delay: 0s; }
        .edu-icon-2 { top: 25%; right: 15%; animation-delay: 3s; }
        .edu-icon-3 { bottom: 20%; left: 20%; animation-delay: 6s; }
        .edu-icon-4 { bottom: 30%; right: 10%; animation-delay: 9s; }
        .edu-icon-5 { top: 50%; left: 50%; animation-delay: 12s; }

        @keyframes eduFloat {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          33% { transform: translateY(-30px) rotate(5deg); }
          66% { transform: translateY(15px) rotate(-5deg); }
        }

        .edu-slide-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(45deg, rgba(0,0,0,0.1), rgba(0,0,0,0.2));
          pointer-events: none;
        }

        /* Buttons */
        .edu-btn-primary {
          border-radius: 4px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
          padding: 12px 30px;
          border: none;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .edu-btn-primary:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.2);
        }

        .edu-btn-primary::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          transition: 0.5s;
        }

        .edu-btn-primary:hover::before {
          left: 100%;
        }

        .edu-btn-outline {
          border-radius: 4px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
          padding: 12px 30px;
          border: 2px solid #fff;
          background: transparent;
          color: #fff;
          transition: all 0.3s ease;
        }

        .edu-btn-outline:hover {
          background: #fff;
          color: var(--edu-primary);
          transform: translateY(-3px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.2);
        }

        /* Progress Bar */
        .edu-scroll-progress {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 4px;
          background: rgba(255, 255, 255, 0.1);
          z-index: 9999;
        }

        .edu-progress-fill {
          height: 100%;
          background: linear-gradient(90deg, var(--edu-primary), var(--edu-accent), var(--edu-success));
          transition: width 0.1s ease;
          position: relative;
          overflow: hidden;
        }

        .edu-progress-fill::after {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
          animation: eduShine 2s infinite;
        }

        @keyframes eduShine {
          0% { left: -100%; }
          100% { left: 100%; }
        }

        /* Cards */
        .edu-card-wrapper {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.6s ease-out;
        }

        .edu-card-wrapper.edu-visible {
          opacity: 1;
          transform: translateY(0);
        }

        .edu-card {
          border: none;
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.3s ease;
          box-shadow: 0 5px 15px rgba(0,0,0,0.1);
          height: 100%;
          position: relative;
        }

        .edu-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 15px 30px rgba(0,0,0,0.15);
        }

        .edu-card-line {
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 3px;
          background: #fff;
          transition: width 0.3s ease;
        }

        .edu-card:hover .edu-card-line {
          width: 80%;
        }

        /* Feature Cards */
        .edu-feature-card {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          text-align: center;
          box-shadow: 0 5px 15px rgba(0,0,0,0.1);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .edu-feature-card:hover {
          transform: translateY(-10px) scale(1.02);
          box-shadow: 0 15px 30px rgba(0,0,0,0.15);
        }

        .edu-feature-icon {
          margin-bottom: 1.5rem;
        }

        .edu-icon-wrapper {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
          font-size: 32px;
          position: relative;
          background: rgba(30, 60, 114, 0.1);
        }

        .edu-icon-glow {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          box-shadow: 0 0 20px rgba(30,60,114,0.3);
          opacity: 0;
          animation: eduGlow 2s ease-in-out infinite;
        }

        .edu-icon-ring {
          position: absolute;
          top: -5px;
          left: -5px;
          right: -5px;
          bottom: -5px;
          border: 2px solid rgba(30,60,114,0.3);
          border-radius: 50%;
          animation: eduRotate 3s linear infinite;
        }

        @keyframes eduGlow {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }

        @keyframes eduRotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Section Styles */
        .edu-section-header {
          position: relative;
          display: inline-block;
        }

        .edu-section-icon {
          position: absolute;
          top: -20px;
          left: -30px;
          font-size: 40px;
          color: var(--edu-primary);
          opacity: 0.1;
        }

        .edu-section-title {
          font-size: 2.5rem;
          font-weight: 700;
          color: var(--edu-dark);
          margin-bottom: 1rem;
        }

        .edu-section-subtitle {
          font-size: 1.1rem;
          color: #666;
          max-width: 600px;
          margin: 0 auto;
        }

        /* Image Styles */
        .edu-image-container {
          position: relative;
        }

        .edu-image-wrapper {
          position: relative;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 20px 40px rgba(0,0,0,0.2);
        }

        .edu-image {
          width: 100%;
          height: auto;
          display: block;
          transition: transform 0.3s ease;
        }

        .edu-image:hover {
          transform: scale(1.05);
        }

        .edu-image-frame {
          position: absolute;
          top: -10px;
          left: -10px;
          right: -10px;
          bottom: -10px;
          border: 2px solid rgba(255,255,255,0.3);
          border-radius: 16px;
          pointer-events: none;
        }

        /* Stats Container */
        .edu-stats-container {
          display: flex;
          gap: 2rem;
          margin-top: 2rem;
        }

        .edu-stat-item {
          text-align: center;
        }

        .edu-stat-item h3 {
          font-size: 2.5rem;
          font-weight: 700;
          color: #fff;
        }

        /* Testimonial Cards */
        .edu-testimonial-card {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          box-shadow: 0 5px 15px rgba(0,0,0,0.1);
          position: relative;
          overflow: hidden;
        }

        .edu-testimonial-quote {
          font-size: 2rem;
          color: var(--edu-primary);
          opacity: 0.2;
          margin-bottom: 1rem;
        }

        .edu-testimonial-text {
          font-style: italic;
          line-height: 1.6;
          margin-bottom: 1.5rem;
        }

        .edu-testimonial-author {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .edu-author-image {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          overflow: hidden;
        }

        /* Modal Styles */
        .edu-modal .modal-content {
          border-radius: 12px;
          border: none;
          box-shadow: 0 20px 40px rgba(0,0,0,0.2);
        }

        .edu-modal-header {
          background: linear-gradient(135deg, var(--edu-primary) 0%, var(--edu-secondary) 100%);
        }

        /* CTA Section */
        .edu-cta-section {
          background: linear-gradient(135deg, var(--edu-primary) 0%, var(--edu-secondary) 50%, var(--edu-accent) 100%);
          background-size: 200% 200%;
          animation: eduGradientShift 15s ease infinite;
        }

        .edu-cta-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0,0,0,0.3);
          pointer-events: none;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .edu-hero-title {
            font-size: 2.5rem;
          }

          .edu-hero-subtitle {
            font-size: 1.2rem;
          }

          .edu-section-title {
            font-size: 2rem;
          }

          .edu-stats-container {
            flex-direction: column;
            gap: 1rem;
          }

          .edu-floating-icon {
            display: none;
          }

          .edu-hero-buttons {
            flex-direction: column;
            gap: 1rem;
          }

          .edu-hero-buttons .btn {
            width: 100%;
            margin: 0.5rem 0;
          }
        }

        @media (max-width: 576px) {
          .edu-hero-title {
            font-size: 2rem;
          }

          .edu-hero-subtitle {
            font-size: 1rem;
          }

          .edu-carousel-slide,
          .edu-default-hero {
            height: 60vh;
            min-height: 400px;
          }

          .edu-hero-section {
            margin-top: -90px;
            padding-top: 90px;
          }
        }

        /* Floating Background Icons */
        .edu-floating-icons {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
        }

        .edu-floating-bg-icon {
          position: absolute;
          font-size: 5rem;
          color: rgba(30, 60, 114, 0.05);
          animation: eduFloatSlow 20s ease-in-out infinite;
        }

        .edu-bg-icon-1 { top: 10%; left: 5%; animation-delay: 0s; }
        .edu-bg-icon-2 { top: 20%; right: 10%; animation-delay: 5s; }
        .edu-bg-icon-3 { bottom: 15%; left: 15%; animation-delay: 10s; }
        .edu-bg-icon-4 { bottom: 25%; right: 5%; animation-delay: 15s; }
        .edu-bg-icon-5 { top: 30%; left: 20%; animation-delay: 2s; }
        .edu-bg-icon-6 { top: 40%; right: 15%; animation-delay: 7s; }
        .edu-bg-icon-7 { bottom: 30%; left: 10%; animation-delay: 12s; }
        .edu-bg-icon-8 { bottom: 20%; right: 20%; animation-delay: 17s; }

        @keyframes eduFloatSlow {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          25% { transform: translateY(-20px) rotate(2deg); }
          50% { transform: translateY(10px) rotate(-2deg); }
          75% { transform: translateY(-10px) rotate(1deg); }
        }

        /* Pattern Overlay */
        .edu-pattern-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-image: 
            linear-gradient(45deg, rgba(255,255,255,0.05) 25%, transparent 25%),
            linear-gradient(-45deg, rgba(255,255,255,0.05) 25%, transparent 25%);
          background-size: 60px 60px;
          pointer-events: none;
        }
      `}</style>
    </>
  );
};

export default Home;